import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Plus, CheckCircle2, Eye, ArrowLeft } from 'lucide-react';
import { useVersions, useDeleteVersion } from '@/hooks/useVersion';
import { useVersionStore } from '@/stores/versionStore';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { VersionTable } from './VersionTable';
import { VersionForm } from './VersionForm';
import { VersionPublish } from './VersionPublish';
import { ErrorDisplay } from '@/components/common/ErrorBoundary';
import { toast } from "sonner";
import { Version } from '../types';
import { useRegion } from '@/hooks/useRegions';

export function VersionList() {
  const { providerId, regionId } = useParams<{ providerId: string, regionId?: string }>();
  const parsedProviderId = Number(providerId);
  const parsedRegionId = regionId ? Number(regionId) : undefined;
  const navigate = useNavigate();
  
  const { data: versions = [], isLoading, error } = useVersions(parsedProviderId, parsedRegionId);
  const deleteVersion = useDeleteVersion(parsedProviderId);
  const { openCreateModal, openPublishModal, setSelectedVersion } = useVersionStore();
  
  const { data: regionData } = useRegion(parsedRegionId || 0);

  const handleDelete = async (id: number, force: boolean) => {
    try {
      const versionToDelete = versions.find(v => v.id === id);
      
      if (!versionToDelete) {
        toast.error("Version not found");
        return;
      }

      if (!deleteVersion.isPending) {
        await deleteVersion.mutateAsync({
          versionId: id,
          force
        });

        toast.success("Version deleted successfully");
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to delete version");
    }
  };

  const handleCreate = () => {
    const hasDraft = versions.some(v => v.status === 'draft');
    if (hasDraft) {
      toast.error("A draft version already exists. Please publish or delete it first.");
      return;
    }

    openCreateModal();
  };

  const handlePublish = (version: Version) => {
    const hasDraft = versions.some(v => v.status === 'draft' && v.id !== version.id);
    if (hasDraft) {
      toast.error("Another draft version already exists. Please publish or delete it first.");
      return;
    }

    setSelectedVersion(version);
    openPublishModal();
  };

  const handlePreview = (version: Version) => {
    if (version.status === 'published') {
      navigate(`/providers/${providerId}/versions/${version.id}/preview`);
    }
  };

  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorDisplay error={error} />;

  const draftVersion = versions.find(v => v.status === 'draft');

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          {regionId && regionData && (
            <div className="mb-2 flex items-center">
              <Button 
                variant="ghost" 
                size="sm" 
                className="mr-2"
                onClick={() => navigate(`/providers/${providerId}/regions`)}
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back to Regions
              </Button>
            </div>
          )}
          <h1 className="text-2xl font-bold">
            {regionId && regionData ? `Versions for ${regionData.name}` : 'Versions'}
            {regionId && regionData?.zip_code && (
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                Zip Code: {regionData.zip_code}
              </span>
            )}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {draftVersion && (
            <Button onClick={() => handlePublish(draftVersion)}>
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Publish Version
            </Button>
          )}
          {versions.find(v => v.status === 'published') && (
            <Button 
              variant="outline" 
              onClick={() => handlePreview(versions.find(v => v.status === 'published')!)}
            >
              <Eye className="h-4 w-4 mr-2" />
              Preview Published
            </Button>
          )}
          <Button 
            variant="outline" 
            onClick={handleCreate}
          >
            <Plus className="h-4 w-4 mr-2" />
            {regionId ? `Create Version for ${regionData?.name || 'Region'}` : 'Create Version'}
          </Button>
        </div>
      </div>
      <VersionTable 
        versions={versions} 
        onDelete={handleDelete}
        isDeleting={deleteVersion.isPending}
      />
      <VersionForm />
      <VersionPublish />
    </div>
  );
} 